import { Flex, Text } from "@hackthenorth/north";
import { GraphQLError } from "graphql";
import { Button as ButtonNew, Spinner, TextInput } from "north.js";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Icon, Modal, errorToast } from "src/shared/components";
import { useHackerContext, useNetworkingContext } from "src/shared/contexts";
import { Field } from "src/shared/contexts/HackerContext/types";
import { useHackerState } from "src/shared/contexts/HackerContext/useHackerState";
import { useDeviceSize } from "src/shared/hooks";
import {
  isEmail,
  isPhoneNumber,
  isValidDiscordTag,
  isValidSocialMediaURL,
} from "src/shared/utils/validation";

import { ACCEPTED_FIELDS, VALIDATORS } from "./constants";

const SOCIAL_PLATFORM_BY_FIELD: Partial<Record<Field, string>> = {
  [Field.INSTAGRAM]: "instagram",
  [Field.FACEBOOK]: "facebook",
  [Field.TWITTER]: "x",
  [Field.LINKEDIN]: "linkedin",
  [Field.GITHUB]: "github",
  [Field.TIKTOK]: "tiktok",
};

const isFieldValueValid = (field: Field, value?: string) => {
  if (!value) return false;
  if (field === Field.DISCORD_TAG) return isValidDiscordTag(value);
  if (field === Field.NETWORKING_PHONE) return isPhoneNumber(value);
  if (field === Field.NETWORKING_EMAIL) return isEmail(value);
  const platform = SOCIAL_PLATFORM_BY_FIELD[field];
  return platform ? isValidSocialMediaURL(value, platform) : false;
};

const getFieldError = (field: Field, label: string, value?: string) => {
  if (!value) return undefined;
  if (isFieldValueValid(field, value)) return undefined;
  if (field === Field.DISCORD_TAG) return "Please enter a valid Discord Tag";
  if (field === Field.NETWORKING_PHONE)
    return "Please enter a valid phone number";
  if (field === Field.NETWORKING_EMAIL) return "Please enter a valid email";
  return `Please enter a valid ${label} URL`;
};

const getFieldPlaceholder = (field: Field, label: string) => {
  if (field === Field.DISCORD_TAG) return "your_discord_username";
  if (field === Field.NETWORKING_PHONE) return "Enter phone number";
  if (field === Field.NETWORKING_EMAIL) return "Enter email";
  return `https://${label.toLowerCase()}.com/your_profile_information`;
};

const Socials = () => {
  const { updateResponses, isLoading } = useHackerContext();
  const { responsesState } = useHackerState(ACCEPTED_FIELDS, VALIDATORS);
  const isTabletOrSmaller = useDeviceSize("tablet");
  const [selectedField, setSelectedField] = useState(Field.INSTAGRAM);
  const [showModal, setShowModal] = useState(false);
  const [value, setValue] = useState<string | undefined>("");
  const [modalTitle, setModalTitle] = useState("");

  const { isLoading: isExtendedProfileLoading } = useNetworkingContext();

  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    setIsValid(isFieldValueValid(selectedField, value));
  }, [selectedField, value]);

  const isPageLoading = isLoading || isExtendedProfileLoading;

  const onSave = async () => {
    if (isValid) {
      try {
        const responses = {
          [selectedField]: value,
        };

        const { errors } = await updateResponses(responses);

        if (errors) {
          throw new Error(
            "We weren't able to submit your answers, please try again."
          );
        }
      } catch (e) {
        if (typeof e === "object") {
          errorToast((e as GraphQLError)?.message);
        } else {
          errorToast((e as GraphQLError)?.message);
        }
      }
    }
  };

  const fieldError = getFieldError(selectedField, modalTitle, value);
  const fieldPlaceholder = getFieldPlaceholder(selectedField, modalTitle);

  return (
    <SocialsWrapper>
      {isTabletOrSmaller ? (
        <MobileModal isOpen={showModal} title={"Enter your " + modalTitle}>
          <LinkInput
            size="md"
            placeholder={fieldPlaceholder}
            error={fieldError}
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setValue(e.target.value);
            }}
          />
          <Buttons>
            <ButtonNew
              color="secondary"
              size="md"
              onClick={() => {
                setValue("");
                setShowModal(false);
              }}
            >
              Cancel
            </ButtonNew>
            <ButtonNew
              color="primary"
              size="md"
              onClick={() => {
                onSave();
                setShowModal(false);
              }}
              disabled={!isValid}
            >
              Confirm
            </ButtonNew>
          </Buttons>
        </MobileModal>
      ) : (
        <Modal
          isOpen={showModal}
          title={modalTitle}
          actions={
            <>
              <ButtonNew
                color="tertiary"
                size="md"
                onClick={() => {
                  setValue("");
                  setShowModal(false);
                }}
              >
                Cancel
              </ButtonNew>
              <ButtonNew
                color="primary"
                size="md"
                onClick={() => {
                  onSave();
                  setShowModal(false);
                }}
                disabled={!isValid}
              >
                Confirm
              </ButtonNew>
            </>
          }
        >
          <LinkInput
            size="md"
            placeholder={fieldPlaceholder}
            error={fieldError}
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setValue(e.target.value);
            }}
          />
        </Modal>
      )}
      <Text mods="heading h3 bold">Socials</Text>

      {isPageLoading ? (
        <Spinner />
      ) : (
        <Flex column gap="sm">
          {[
            {
              field: Field.NETWORKING_PHONE,
              label: "Phone number",
              value: responsesState.networking_phone_number,
            },
            {
              field: Field.NETWORKING_EMAIL,
              label: "Email",
              value: responsesState.networking_email,
            },
            {
              field: Field.INSTAGRAM,
              label: "Instagram",
              value: responsesState.networking_instagram_link,
            },
            {
              field: Field.FACEBOOK,
              label: "Facebook",
              value: responsesState.networking_facebook_link,
            },
            {
              field: Field.TWITTER,
              label: "X",
              value: responsesState.networking_twitter_link,
            },
            {
              field: Field.LINKEDIN,
              label: "LinkedIn",
              value: responsesState.networking_linkedin_link,
            },
            {
              field: Field.GITHUB,
              label: "Github",
              value: responsesState.networking_github_link,
            },
            {
              field: Field.DISCORD_TAG,
              label: "Discord Tag",
              value: responsesState.networking_discord_tag,
            },
            {
              field: Field.TIKTOK,
              label: "TikTok",
              value: responsesState.networking_tiktok_link,
            },
          ].map(({ field, label, value }) => (
            <Row
              key={field}
              onClick={() => {
                setSelectedField(field);
                setValue(value || "");
                setModalTitle(label);
                setShowModal(true);
              }}
            >
              <Text mods="body">{label}</Text>
              <Text className="handle">{value || "-"}</Text>
              <Icon name="edit" size="h3" />
            </Row>
          ))}
        </Flex>
      )}
    </SocialsWrapper>
  );
};

const LinkInput = styled(TextInput)`
  width: 100%;
`;

const SocialsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 0;
`;

const MobileModal = styled(Modal)`
  width: 100%;
  height: 80vh;
  position: fixed;
  bottom: 0;
  background: white;
  z-index: 100000 !important;
  border-radius: 24px 24px 0px 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: slideUp 0.2s;
  text-align: center;

  div {
    width: 100%;
  }

  @keyframes slideUp {
    0% {
      bottom: -60vh;
    }
    100% {
      bottom: 0;
    }
  }
`;

const Buttons = styled.div`
  display: flex;
  margin-top: 30px;
  justify-content: space-between;
  button {
    width: 45%;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) =>
      theme.globalConstants.color.backgroundGray};
  }

  .handle {
    flex-grow: 1;
    text-align: right;
    margin-right: 12px;
    overflow-wrap: break-word;
    word-break: break-word;
    color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  }

  svg {
    flex-shrink: 0;
  }
`;

export default Socials;
